// Serialises an Artifact to markdown that survives leaving the app.
//
// Why this exists: the synthesizer prompt tells the model to emit `table`
// artifacts as HTML tables, and HtmlArtifact was the one renderer with no
// download at all. A table rendered perfectly and could not be exported —
// the only way out was "show as text", which hands you raw <table> markup.
// Saved as .md that opens as exactly what was reported: text and badly-formed
// tables.
//
// Note the payload for a `table` artifact can arrive as HTML, as GFM pipes, or
// (from some tools) as JSON. All three are handled.

import Papa from "papaparse";

import type { Artifact } from "@/types/eryndal";

/** Escapes the only character that can break out of a GFM table cell. */
function cell(value: string): string {
  return value.replace(/\|/g, "\\|").replace(/\r?\n/g, " ").trim();
}

function toPipeTable(rows: string[][]): string {
  const clean = rows.filter((r) => r.length > 0);
  if (clean.length === 0) return "";

  const width = Math.max(...clean.map((r) => r.length));
  const pad = (r: string[]) => [...r, ...Array(width - r.length).fill("")];

  const [header, ...body] = clean.map(pad);
  const lines = [
    `| ${header.map(cell).join(" | ")} |`,
    `| ${header.map(() => "---").join(" | ")} |`,
    ...body.map((r) => `| ${r.map(cell).join(" | ")} |`),
  ];
  return lines.join("\n");
}

/** HTML <table> → GFM. Uses the platform parser; no dependency needed. */
function htmlTableToMarkdown(html: string): string | null {
  if (typeof DOMParser === "undefined") return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) return null;

  const rows = [...table.querySelectorAll("tr")].map((tr) =>
    [...tr.querySelectorAll("th, td")].map((c) => c.textContent ?? ""),
  );
  const table_ = toPipeTable(rows);
  return table_ || null;
}

/** A JSON payload shaped like {columns, rows} or a bare array of objects. */
function jsonTableToMarkdown(payload: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return null;
  }

  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const obj = parsed as { columns?: unknown; rows?: unknown };
    if (Array.isArray(obj.columns) && Array.isArray(obj.rows)) {
      const header = obj.columns.map((c) => String(c));
      const body = obj.rows.map((r) => (Array.isArray(r) ? r.map((v) => String(v)) : [String(r)]));
      return toPipeTable([header, ...body]);
    }
  }

  if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "object") {
    const keys = [...new Set(parsed.flatMap((r) => Object.keys(r as object)))];
    const body = (parsed as Record<string, unknown>[]).map((r) =>
      keys.map((k) => (r[k] == null ? "" : String(r[k]))),
    );
    return toPipeTable([keys, ...body]);
  }

  return null;
}

function csvToMarkdown(payload: string): string {
  // papaparse is already a dependency and already parses CsvArtifact.
  const parsed = Papa.parse<string[]>(payload, { skipEmptyLines: true });
  return toPipeTable(parsed.data ?? []);
}

function looksLikePipeTable(payload: string): boolean {
  return !/<table[\s>]/i.test(payload) && /\|.*\|/.test(payload);
}

function fence(body: string, lang = ""): string {
  return ["```" + lang, body.trim(), "```"].join("\n");
}

/**
 * Converts one artifact to a markdown block. Always returns something usable —
 * an unrecognised payload falls back to a fenced code block rather than being
 * dropped, so an export never silently loses content.
 */
export function artifactToMarkdown(artifact: Artifact): string {
  const heading = artifact.title?.trim() ? `### ${artifact.title.trim()}\n\n` : "";
  const payload = artifact.payload ?? "";

  const body = (() => {
    switch (artifact.kind) {
      case "markdown":
        return payload;

      case "csv":
        return csvToMarkdown(payload);

      case "mermaid":
        return fence(payload, "mermaid");

      case "cytoscape":
        try {
          return fence(JSON.stringify(JSON.parse(payload), null, 2), "json");
        } catch {
          return fence(payload, "json");
        }

      case "xlsx": {
        const url = artifact.metadata?.download_url as string | undefined;
        return url
          ? `Spreadsheet export: [download .xlsx](${url})`
          : "_Spreadsheet artifact — download it from the artifacts drawer._";
      }

      case "html":
      case "table":
        // Order matters: HTML first, because an HTML table can also contain
        // pipe characters in its cell text.
        return (
          htmlTableToMarkdown(payload) ??
          (looksLikePipeTable(payload) ? payload : null) ??
          jsonTableToMarkdown(payload) ??
          fence(payload, "html")
        );

      default:
        return payload;
    }
  })();

  return `${heading}${body}`.trim();
}

interface AnswerDocumentInput {
  query: string;
  answer: string;
  artifacts?: Artifact[];
  citations?: { title?: string; ref?: string; id?: string; url?: string }[];
  groundingScore?: number | null;
}

/**
 * Builds the whole answer as one self-contained markdown document.
 *
 * Artifacts live only in the drawer now, so an export of just the prose would
 * silently omit everything the run actually built.
 */
export function answerToMarkdown({
  query,
  answer,
  artifacts = [],
  citations = [],
  groundingScore,
}: AnswerDocumentInput): string {
  const parts: string[] = [];

  parts.push(`# ${query.trim()}`);
  parts.push(answer.trim());

  if (artifacts.length) {
    parts.push("## Artifacts");
    for (const a of artifacts) parts.push(artifactToMarkdown(a));
  }

  if (citations.length) {
    parts.push("## Sources");
    parts.push(
      citations
        .map((c, i) => {
          const label = c.title ?? c.ref ?? c.id ?? "Unnamed source";
          return c.url ? `${i + 1}. [${label}](${c.url})` : `${i + 1}. ${label}`;
        })
        .join("\n"),
    );
  }

  if (groundingScore !== undefined && groundingScore !== null) {
    parts.push(`_Grounding score: ${groundingScore.toFixed(2)}_`);
  }

  return parts.join("\n\n") + "\n";
}
